import { useCallback, useState } from "react";

import { ProviderService } from "@asd/customer-portal-api";
import type { GridFilterModel, GridLogicOperator, GridSortModel } from "@mui/x-data-grid/models";

import { AddressOption } from "~/components/shared/AddressPicker";
import { ProviderRegistrationForDisplay } from "~/utils/providerRegistrationHelpers";
import { MapLocationType } from "./map/MapView";

export type SearchFilters = {
  serviceAddress: AddressOption | null;
  services: string[];
};

type CurrentSearch = {
  searchAddress: string;
  services: ProviderService[];
};

export type TableViewState = {
  expandedIds: unknown[];
  columnWidths: Record<string, number>;
  searchTerm: string;
  paginationModel: { pageSize: number; page: number };
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
};

export type MapViewState = {
  location: MapLocationType;
  providers: ProviderRegistrationForDisplay[];
  currentSearch: CurrentSearch | null;
  selectedProvider: ProviderRegistrationForDisplay | null;
  providerListScrollTop: number;
};

const defaultTableViewState: TableViewState = {
  expandedIds: [],
  columnWidths: {},
  searchTerm: "",
  paginationModel: { pageSize: 25, page: 0 },
  sortModel: [],
  filterModel: { items: [], logicOperator: "and" as GridLogicOperator },
};

const defaultMapViewState: MapViewState = {
  location: { coordinates: [-91.14614611039785, 43.051471023598786] },
  providers: [],
  currentSearch: null,
  selectedProvider: null,
  providerListScrollTop: 0,
};

type PersistedState = {
  showDisclaimer: boolean;
  view: "map" | "table";
  searchFilters: SearchFilters;
  mapViewState: MapViewState;
  tableViewState: TableViewState;
};

const defaultState: PersistedState = {
  showDisclaimer: true,
  view: "map",
  searchFilters: { serviceAddress: null, services: [] },
  mapViewState: defaultMapViewState,
  tableViewState: defaultTableViewState,
};

let persistedState: PersistedState = { ...defaultState };

export const useCareProvidersState = () => {
  const [state, setStateInternal] = useState<PersistedState>(persistedState);

  const setShowDisclaimer = useCallback((showDisclaimer: boolean) => {
    const next = { ...persistedState, showDisclaimer };
    persistedState = next;
    setStateInternal(next);
  }, []);

  const setView = useCallback((view: "map" | "table") => {
    const next = { ...persistedState, view };
    persistedState = next;
    setStateInternal(next);
  }, []);

  const setSearchFilters = useCallback((action: { key: keyof SearchFilters; value: any }) => {
    const next = {
      ...persistedState,
      searchFilters: { ...persistedState.searchFilters, [action.key]: action.value },
      mapViewState: { ...persistedState.mapViewState, providers: [], currentSearch: null },
    };
    persistedState = next;
    setStateInternal(next);
  }, []);

  const setMapViewState = useCallback((patch: Partial<MapViewState>) => {
    const next = {
      ...persistedState,
      mapViewState: { ...persistedState.mapViewState, ...patch },
    };
    persistedState = next;
    setStateInternal(next);
  }, []);

  const setTableViewState = useCallback((patch: Partial<TableViewState>) => {
    const next = {
      ...persistedState,
      tableViewState: { ...persistedState.tableViewState, ...patch },
    };
    persistedState = next;
    setStateInternal(next);
  }, []);

  return {
    showDisclaimer: state.showDisclaimer,
    view: state.view,
    searchFilters: state.searchFilters,
    mapViewState: state.mapViewState,
    tableViewState: state.tableViewState,
    setShowDisclaimer,
    setView,
    setSearchFilters,
    setMapViewState,
    setTableViewState,
  };
};
